import React, {
  memo, useCallback, useEffect, useState
} from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useForm } from 'react-hook-form';
import RightModalLayout from "../layouts/Modal/RightModalLayout";
import { SIDEBAR_ACTIONS } from "../../constants/actionsConstants";
import WhatsappFlowModelMC from "../models/WhatsappFlowModelMC";
import SendMessageModelMC from "../models/inbox/SendMessageModelMC";
import { isInboxMsChat } from "../../services/inbox/inboxMsChatPlatform";
import ButtonMC from "../components/ButtonMC";
import LoadingMC from "../components/LoadingMC";
import FormProviderMC from "../components/FormProviderMC";
import InputMC from "../components/InputMC";

const DEFAULT_BODY = '¡Hola! Nos encantaría conocer tu opinión sobre tu experiencia de compra.';
const DEFAULT_FOOTER = 'Powered by Mercately';

const WhatsappFlowsModal = memo(({ customer, platform, conversation }) => {
  const { t } = useTranslation('InboxMC');
  const dispatch = useDispatch();
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [previewFlow, setPreviewFlow] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const methods = useForm({
    defaultValues: {
      body_text: DEFAULT_BODY,
      footer_text: DEFAULT_FOOTER,
      flow_cta: t('chat.flow_cta_default')
    }
  });

  const closeModal = useCallback(() => {
    dispatch({
      type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR,
      payload: { componentKey: 'WhatsappFlowsModal' }
    });
  }, [dispatch]);

  useEffect(() => {
    const fetchFlows = async () => {
      try {
        const data = await WhatsappFlowModelMC.get({ status: 'PUBLISHED' });
        setFlows(data?.whatsapp_flows || []);
      } catch (error) {
        console.error("Error fetching flows", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFlows();
  }, []);

  const handleSelectFlow = useCallback((flow) => {
    setSelectedFlow(flow);
    methods.reset({
      body_text: flow.default_body_text || DEFAULT_BODY,
      footer_text: flow.default_footer_text || DEFAULT_FOOTER,
      flow_cta: flow.default_flow_cta || t('chat.flow_cta_default')
    });
  }, [methods, t]);

  const handleShowPreview = useCallback(async (flow) => {
    setPreviewFlow(flow);
    setPreviewLoading(true);
    setPreviewUrl(null);
    try {
      const detail = await WhatsappFlowModelMC.getDetail(flow.meta_flow_id);
      const url = detail?.preview_url || flow.preview_url;
      setPreviewUrl(url || null);
    } catch (e) {
      console.error('Error fetching preview', e);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewFlow(null);
    setPreviewUrl(null);
  }, []);

  const handleBack = useCallback(() => {
    setSelectedFlow(null);
  }, []);

  const handleSend = async (data) => {
    setSending(true);
    try {
      const flowCta = data.flow_cta || t('chat.flow_cta_default');

      const headerText = selectedFlow.default_header_text;

      if (isInboxMsChat(platform)) {
        // The chat lives in the inbox microservice, so the send has to go
        // through it: a message persisted by Rails would never reach this
        // thread. `SendMessageModelMC` already resolves the chat id and points
        // at the MS, so it stays the single place that knows how to talk to it.
        await SendMessageModelMC.sendMessage({
          chat_id: conversation?.id,
          platform,
          type: 'flow',
          flow_id: selectedFlow.meta_flow_id,
          header_text: headerText,
          message: data.body_text,
          footer_text: data.footer_text,
          flow_cta: flowCta,
          flow_action: "navigate",
          flow_action_payload: {
            screen: "WELCOME_SCREEN",
            data: {}
          }
        });
        // Rails is out of the send path here, so the event it used to track on
        // `send_message` has to be reported on its own.
        WhatsappFlowModelMC
          .trackFlowSent(selectedFlow.meta_flow_id)
          .catch(() => {});
      } else {
        await WhatsappFlowModelMC.sendMessage({
          customer_id: customer.id,
          flow_id: selectedFlow.meta_flow_id,
          header_text: headerText,
          body_text: data.body_text,
          footer_text: data.footer_text,
          flow_cta: flowCta,
          flow_action: "navigate",
          flow_action_payload: {
            screen: "WELCOME_SCREEN",
            data: {}
          }
        });
      }

      if (window.showtoast) window.showtoast(t('chat.flow_sent_success'));
      closeModal();
    } catch (error) {
      console.error("Error sending flow", error);
      if (window.showtoast) window.showtoast(t('chat.flow_send_error'), 'error');
    } finally {
      setSending(false);
    }
  };

  const renderFlowList = () => {
    if (loading) {
      return (
        <div className="tw-py-10">
          <LoadingMC loading />
        </div>
      );
    }

    if (flows.length === 0) {
      return (
        <p className="tw-text-gray-500 tw-text-center tw-py-10">
          {t('chat.no_flows')}
        </p>
      );
    }

    return (
      <>
        {flows.map(flow => {
          const flowTitle = flow.default_header_text;

          return (
            <div
              key={flow.id}
              className="tw-flex tw-flex-row tw-items-center tw-justify-between tw-border tw-border-gray-200 tw-rounded-lg tw-p-3 tw-bg-white hover:tw-bg-gray-50 tw-transition-colors"
            >
              <div className="tw-flex tw-flex-col tw-truncate tw-pr-3">
                <span className="tw-font-medium tw-text-gray-800 tw-truncate" title={flowTitle}>
                  {flowTitle}
                </span>
                <span className="tw-text-xs tw-text-gray-500 tw-mt-1">
                  {flow.status}{flow.categories?.length > 0 && ` • ${flow.categories[0]}`}
                </span>
              </div>
              <div className="tw-flex tw-gap-2 tw-shrink-0">
                <ButtonMC
                  text={t('chat.preview_flow')}
                  size="small"
                  variant="SECONDARY"
                  outlined
                  icon="eye-outline"
                  iconPosition="left"
                  onClick={() => handleShowPreview(flow)}
                />
                <ButtonMC
                  text={t('chat.select_flow')}
                  size="small"
                  variant="SECONDARY"
                  onClick={() => handleSelectFlow(flow)}
                />
              </div>
            </div>
          );
        })}
      </>
    );
  };

  const renderSendForm = () => (
    <FormProviderMC methods={methods}>
      <InputMC
        useFloatingLabel
        name="flow_name"
        label={t('chat.flow_title')}
        value={selectedFlow.default_header_text || ''}
        disabled
        className="tw-mb-4"
        icon="whatsapp-fill-outline"
      />

      <InputMC
        useFloatingLabel
        textarea
        rows={4}
        name="body_text"
        label={t('chat.flow_body')}
        placeholder={t('chat.flow_body_placeholder')}
        className="tw-mb-4"
      />

      <InputMC
        useFloatingLabel
        name="footer_text"
        label={t('chat.flow_footer')}
        placeholder={t('chat.flow_footer_placeholder')}
        className="tw-mb-4"
      />

      <InputMC
        useFloatingLabel
        name="flow_cta"
        label={t('chat.flow_cta_label')}
        placeholder={t('chat.flow_cta_placeholder')}
        className="tw-mb-4"
      />
    </FormProviderMC>
  );

  const title = selectedFlow
    ? t('chat.send_flow')
    : t('chat.whatsapp_flows');

  return (
    <RightModalLayout
      title={title}
      closeAction={closeModal}
      submitAction={selectedFlow ? methods.handleSubmit(handleSend) : null}
      cancelAction={selectedFlow ? handleBack : null}
      submitLabel={t('chat.send')}
      cancelLabel={t('chat.back')}
      submitDisabled={sending}
      footerVariant="HORIZONTAL"
      footerClassName="tw-border-none"
    >
      {selectedFlow ? renderSendForm() : renderFlowList()}

      {/* Preview overlay */}
      {previewFlow && (
        <div className="tw-absolute tw-inset-0 tw-z-10 tw-flex tw-flex-col tw-bg-white">
          <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-gray-200 tw-px-4 tw-py-3">
            <span className="tw-font-semibold tw-text-gray-800 tw-text-sm">
              {t('chat.preview_flow')} — {previewFlow.name}
            </span>
            <button
              type="button"
              onClick={handleClosePreview}
              className="tw-text-gray-500 hover:tw-text-gray-700 tw-text-lg"
              aria-label="Cerrar preview"
            >
              ✕
            </button>
          </div>
          <div className="tw-flex tw-flex-1 tw-items-center tw-justify-center tw-overflow-y-auto tw-bg-gray-50 tw-p-4">
            {previewLoading && <LoadingMC loading />}

            {!previewLoading && previewUrl && (
              <iframe
                src={previewUrl}
                title={`Preview: ${previewFlow.name}`}
                width={490}
                height={720}
                className="tw-rounded-xl tw-shadow-md tw-border tw-border-gray-200 tw-self-start tw-mt-4"
              />
            )}

            {!previewLoading && !previewUrl && (
              <p className="tw-text-gray-400 tw-text-sm tw-text-center">
                {t('chat.preview_unavailable')}
              </p>
            )}
          </div>
        </div>
      )}
    </RightModalLayout>
  );
});

WhatsappFlowsModal.displayName = "WhatsappFlowsModal";

export default WhatsappFlowsModal;
